// August 2026 monthly report companion blog.
import { defineDocument, em, link, p, section, strong, text, } from '../../schema';
const augustReportHref = '/party-central/reports/2026-08-top-party-games-and-streamers';
export const blogPartyGameStreamingByTheNumbersAugust2026Document = defineDocument({
    id: 'blog:party-game-streaming-by-the-numbers-august-2026',
    kind: 'blog',
    slug: 'party-game-streaming-by-the-numbers-august-2026',
    route: '/blog/party-game-streaming-by-the-numbers-august-2026',
    seo: {
        title: 'Party Game Streaming by the Numbers: August 2026',
        description: 'MECCHA CHAMELEON starts to blend in, The Choicer Voicer speaks up, and Smash Bros. players take the top of the August 2026 streamer rankings.',
        imageAsset: 'twitchStats202608',
        imageAlt: 'Collage of top party games and streamers from the August 2026 Twitch data report',
    },
    hero: {
        eyebrow: [text('Airhead Blog')],
        title: [text('Party Game Streaming by the Numbers: August 2026')],
        description: [
            text('MECCHA CHAMELEON starts to blend in, The Choicer Voicer speaks up, and Smash Bros. players take the top of the August 2026 streamer rankings.'),
        ],
        imageAsset: 'twitchStats202608',
        imageAlt: 'Collage of top party games and streamers from the August 2026 Twitch data report',
        caption: 'Top party games and streamers on Twitch for August 2026, as tracked by the Party Central report.',
        showDescription: true,
        background: 'blurred-image',
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-09-23T00:00:00.000Z'),
        updatedAt: new Date('2026-09-23T00:00:00.000Z'),
        tags: ['party-central'],
    },
    sections: [
        section({
            id: 'article',
            blocks: [
                p([
                    text('In August we tracked '),
                    strong('193,339 party game streams'),
                    text(' from '),
                    strong('84,239 streamers'),
                    text(' across '),
                    strong('995 games'),
                    text(' and '),
                    strong('34 languages'),
                    text('. That is 28% fewer streams than July, and '),
                    em('MECCHA CHAMELEON'),
                    text(' accounts for the entire drop. The '),
                    link(augustReportHref, 'full August 2026 report'),
                    text(' has every ranking and table.'),
                ]),
            ],
            sections: [
                section({
                    id: 'article__the-chameleon-changes-colors',
                    title: 'The Chameleon Changes Colors',
                    blocks: [
                        p([
                            em('MECCHA CHAMELEON'),
                            text(' was still the most-streamed party game in August, but it lost about 77,000 streams, three-quarters of its July total. Only 11% of its July streamers came back, and 80% of its August streamers played it just once.'),
                        ]),
                        p([
                            text('The slide lasted all month, from about 1,135 streams a day in the first week to about 500 in the last. Even at 500 a day, it outpaced second-place '),
                            em('Among Us'),
                            text('. In July it was the most-streamed game in each of the top 10 languages. By August it held that spot only in English, German, and Russian.'),
                        ]),
                    ],
                }),
                section({
                    id: 'article__everyone-found-their-voice',
                    title: 'Everyone Found Their Voice',
                    blocks: [
                        p([
                            text('The biggest jump belonged to '),
                            em('The Choicer Voicer'),
                            text(', which grew from 65 streamers in July to 4,705 in August. It is an early-access '),
                            link('https://yeahmaybe.itch.io/the-choicer-voicer', 'voice-impression party game'),
                            text(' from YeahMaybe, with a mode that lets Twitch chat vote.'),
                        ]),
                        p([
                            text('Big creators picked it up one after another. '),
                            link('https://twitch.tv/krokoboss', 'krokoboss'),
                            text(' started on August 6, and over the next three weeks '),
                            link('https://twitch.tv/vanillamace', 'VanillaMace'),
                            text(', '),
                            link('https://twitch.tv/rdcgaming', 'RDCgaming'),
                            text(', '),
                            link('https://twitch.tv/cellbit', 'Cellbit'),
                            text(', '),
                            link('https://twitch.tv/papaplatte', 'Papaplatte'),
                            text(', '),
                            link('https://twitch.tv/jynxzi', 'Jynxzi'),
                            text(', '),
                            link('https://twitch.tv/xqc', 'xQc'),
                            text(', '),
                            link('https://twitch.tv/northernlion', 'Northernlion'),
                            text(', and '),
                            link('https://twitch.tv/vinesauce', 'Vinesauce'),
                            text(' followed. The game drew nearly as many total viewers as '),
                            em('MECCHA CHAMELEON'),
                            text(' from less than a quarter of the streams.'),
                        ]),
                        p([
                            text('Most streamers played it only once, though. Party Score rewards games that people come back to, so '),
                            em('The Choicer Voicer'),
                            text(' missed the top 15.'),
                        ]),
                    ],
                }),
                section({
                    id: 'article__back-on-top',
                    title: 'Back on Top',
                    blocks: [
                        p([
                            em('nanos world'),
                            text(' returned to first place in the Party Score rankings, and nearly all of its August activity came from one French Naruto roleplay server: '),
                            link('https://zenkai-rp.com/qui-sommes-nous', 'Zenkai RP'),
                            text('. French channels made 95% of its streams. '),
                            link('https://twitch.tv/mehditdonc', 'MehDitDonc'),
                            text(' started a near-daily "Épisode" series on August 13 which drew a large audience for the rest of the month.'),
                        ]),
                        p([
                            text('Second place went to '),
                            em('Diablo II: Resurrected'),
                            text(' after '),
                            link('https://news.blizzard.com/en-us/article/24296140/diablo-ii-resurrected-ladder-season-15-coming-soon', 'Ladder Season 15'),
                            text(' opened on August 21. A ladder reset sends everyone back to fresh characters, and average daily streams jumped from less than 200 per day to over 800 after the release.'),
                        ]),
                    ],
                }),
                section({
                    id: 'article__smash-hits',
                    title: 'Smash Hits',
                    blocks: [
                        p([
                            text('Three channels tied for the top Streamer Party Score of 98, and '),
                            link('https://twitch.tv/nohoho12', 'のほほ'),
                            text(' took first on the largest average audience. It is the channel of '),
                            link('https://www.ssbwiki.com/Smasher:NOHOHO', 'NOHOHO'),
                            text(', a Japanese '),
                            em('Super Smash Bros. Ultimate'),
                            text(' player who mains Ness and Lucas and runs the NOHOHO Off tournament series. Fellow Japanese Smash player '),
                            link('https://twitch.tv/kept_varrel', 'kept_VARREL'),
                            text(' tied for the top score, as did '),
                            link('https://twitch.tv/x33n', 'X33N'),
                            text(', who spent 109 of 121 streams on '),
                            em('Words On Stream'),
                            text('.'),
                        ]),
                        p([
                            link('https://twitch.tv/vgbootcamp', 'VGBootCamp'),
                            text(" entered at fifth by broadcasting August's big Smash tournaments: "),
                            link('https://www.ssbwiki.com/Tournament:Supernova_2026', 'Supernova 2026'),
                            text(' in Baltimore and '),
                            link('https://www.ssbwiki.com/Tournament:CEO_2026', 'CEO 2026'),
                            text(" in Orlando. Supernova's finals day drew more than 11,000 viewers, and the channel carried a quarter of all Smash viewers for the month."),
                        ]),
                        p([
                            link('https://twitch.tv/paache_', 'paache_'),
                            text(' reached 13th with only nine streams, averaging about 2,100 viewers each. The Spanish channel runs a series called "Reviviendo la Wii" (reliving the Wii), now past episode 440, and spent August chasing platinum medals in '),
                            em('Wii Sports'),
                            text('.'),
                        ]),
                    ],
                }),
                section({
                    id: 'article__pizza-house-delivers',
                    title: 'Pizza House Delivers',
                    blocks: [
                        p([
                            link('https://store.steampowered.com/app/4132330/Pizza_House_Simulator/', 'Pizza House Simulator'),
                            text(', an online co-op game for up to six players, released on August 19 and reached 878 streamers by the end of the month. Apart from '),
                            em('MECCHA CHAMELEON'),
                            text(', it is one of the strongest new releases we have tracked this year.'),
                        ]),
                        p([
                            text('German creator '),
                            link('https://twitch.tv/trymacs', 'Trymacs'),
                            text(" was a big driver of the game's early success, including a highly watched co-op session with "),
                            link('https://twitch.tv/rumathra', 'Rumathra'),
                            text(', '),
                            link('https://twitch.tv/gtimetv', 'GtimeTV'),
                            text(', and '),
                            link('https://twitch.tv/cezzy', 'Cezzy'),
                            text('.'),
                        ]),
                    ],
                }),
                section({
                    id: 'article__read-the-full-report',
                    title: 'Read the Full Report',
                    blocks: [
                        p([
                            text('There is a lot more in the '),
                            link(augustReportHref, 'full August 2026 report'),
                            text(': complete Party Score rankings, new releases, streamer spotlights, language breakdowns, game deep dives, and streaming patterns by day and hour.'),
                        ]),
                    ],
                }),
            ],
        }),
    ],
});
//# sourceMappingURL=party-game-streaming-by-the-numbers-august-2026.js.map